// src/utils/settlementEngine.simple.js
// Minimal, conservative settlement engine for v1

// If you want to skip tables entirely for now, set USE_TABLES=false.

// Optional: import your tables. If you want “no tables yet” just don't import and use zeros below.
import { EXPENSE_TABLES } from "./useExpenseData";

// ---------- helpers ----------
const toNum = (v, d = 0) => {
  if (v == null || v === "") return d;
  if (typeof v === "number") return Number.isFinite(v) ? v : d;
  const s = String(v).replace(/[,\s$]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : d;
};
const n = (v) => (v === "" || v == null ? 0 : Number(v)) || 0;

function monthsBetween(a, b) {
  // months from date a -> date b (positive if b is after a)
  return (
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
  );
}

// ---------- allowances (very simple) ----------
function determineAllowances(expenseData) {
  const { housing, auto, healthCareExpenses, livingExpenses, states } =
    EXPENSE_TABLES;
  const toNum = (v, d = 0) => {
    if (v == null || v === "") return d;
    const n = Number(String(v).replace(/[,\s$]/g, ""));
    return Number.isFinite(n) ? n : d;
  };

  const state = expenseData.state || "";
  const cars = Math.max(0, toNum(expenseData.cars, 0));
  const residents = Math.max(1, toNum(expenseData.residents, 1));
  const residents65 = Math.min(
    Math.max(0, toNum(expenseData.residents65, 0)),
    residents
  );

  // Auto (regional)
  const region = Object.values(auto).find(
    (r) => Array.isArray(r?.states) && r.states.includes(state)
  );
  const autoCost = region
    ? toNum(cars > 1 ? region.twoCars : region.oneCar)
    : 0;

  // Housing (state x family size)
  const famKey = `familyOf${residents}`;
  const row = Array.isArray(housing)
    ? housing.find((h) => h.state === state)
    : null;
  const housingExpense = row ? toNum(row[famKey]) : 0;

  // Healthcare (per-capita)
  const under65 = Math.max(0, residents - residents65);
  const healthCareExpense =
    toNum(healthCareExpenses.under65) * under65 +
    toNum(healthCareExpenses.over65) * residents65;

  // National living standard
  const living = livingExpenses[famKey] || {};
  const livingOut = {
    food: toNum(living.food),
    housekeepingSupplies: toNum(living.housekeepingSupplies),
    apparelAndServices: toNum(living.apparelAndServices),
    personalCare: toNum(living.personalCare),
    miscellaneous: toNum(living.miscellaneous),
    total: toNum(living.total),
  };

  return {
    autoCost,
    housingExpense,
    healthCareExpense,
    livingExpenses: livingOut,
  };
}

// ---------- simplified derivations ----------
function calcIncome(form) {
  const list = Array.isArray(form?.incomes) ? form.incomes : [];

  const pick = (type) => list.filter((i) => i?.incomeType === type);
  const sum = (arr, sel) =>
    arr.reduce((s, x) => s + Number(sel ? sel(x) : x) || 0, 0);

  const wageIncome = sum(pick("salary"), (i) => i.amount) * 0.8;
  const rentalIncome = sum(pick("rental"), (i) => i.amount) * 0.8;
  const disbursementIncome = sum(pick("passive"), (i) => i.amount) * 0.8; // SSI/retirement/etc.
  const supportIncome = sum(pick("child"), (i) => i.amount) * 0.8; // child support/alimony

  const totalIncome =
    wageIncome + rentalIncome + disbursementIncome + supportIncome;

  return {
    totalIncome,
    wageIncome,
    rentalIncome,
    disbursementIncome,
    supportIncome,
  };
}
function calcLiabilities(form) {
  const rows = Array.isArray(form?.otherLiabilities)
    ? form.otherLiabilities
    : [];

  const totalBalance = n(form?.totalBalance); // IRS total

  const stateRows = rows.filter((r) => r?.liabType === "State Tax Debt");
  const otherRows = rows.filter((r) => r?.liabType !== "State Tax Debt");

  const stateBalance = stateRows.reduce((s, r) => s + n(r.total), 0);
  const statePayment = stateRows.reduce((s, r) => s + n(r.payment), 0);

  const otherBalance = otherRows.reduce((s, r) => s + n(r.total), 0);
  const otherPayment = otherRows.reduce((s, r) => s + n(r.payment), 0);

  return {
    totalBalance,
    stateLiability: {
      stateBalance,
      statePayment,
    },
    otherLiabilities: {
      otherBalance,
      otherPayment,
    },
  };
}
function calcExpenses(allowances, liab) {
  const livingTotal = toNum(allowances?.livingExpenses?.total);
  const allowancesTotal =
    toNum(allowances?.autoCost) +
    toNum(allowances?.housingExpense) +
    toNum(allowances?.healthCareExpense) +
    livingTotal;

  const monthlyPayments =
    toNum(liab?.stateLiability?.statePayment) +
    toNum(liab?.otherLiabilities?.otherPayment);

  return {
    totalExpenses: allowancesTotal + monthlyPayments,
    allowancesTotal,
    monthlyPayments,
    breakdown: {
      auto: toNum(allowances?.autoCost),
      housing: toNum(allowances?.housingExpense),
      healthCare: toNum(allowances?.healthCareExpense),
      living: livingTotal,
      statePayment: toNum(liab?.stateLiability?.statePayment),
      otherPayment: toNum(liab?.otherLiabilities?.otherPayment),
    },
  };
}
function calcAssets(form) {
  const list = Array.isArray(form?.assets) ? form.assets : [];

  let netAssets = 0;
  let liquidAccounts = 0;

  for (const a of list) {
    const value = Number(a?.value || 0);
    const liab = Number(a?.assetLiab || 0);
    const net = Math.max(0, value - liab);

    netAssets += net;

    // liquid buckets we allow to count toward down payment ability
    if (
      a?.assetType === "Savings Account" ||
      a?.assetType === "Retirement Account" ||
      a?.assetType === "Stocks and Bonds"
    ) {
      liquidAccounts += net;
    }
  }

  return { netAssets, liquidAccounts };
}

function calcCollectionWindow(form) {
  const now = new Date();
  const years = Array.isArray(form?.problemYears)
    ? form.problemYears.map((y) => toNum(y)).filter(Boolean)
    : [];

  // No years provided → fallback to “(last year + 9) CSED”
  if (years.length === 0) {
    const end = new Date(now.getFullYear() - 1 + 9, 11, 31);
    const months = Math.max(1, monthsBetween(now, end));
    return { months, expirations: [], dropSchedule: [] };
  }

  // Main CSED is based on newest problem year
  const newest = Math.max(...years);
  const newestEnd = new Date(newest + 10, 11, 31); // Dec 31 of CSED year
  const months = Math.max(1, monthsBetween(now, newestEnd));

  // Expiration years for all selected problem years
  const expirations = years
    .map((y) => y + 10)
    .filter((y) => y >= now.getFullYear()) // ignore already-expired
    .sort((a, b) => a - b);

  // Illustrative drop schedule:
  // Evenly allocate balance across all selected years, and drop each slice
  // when that year’s CSED arrives.
  const total = toNum(form?.totalBalance);
  const perSlice = years.length > 0 ? total / years.length : 0;

  const dropSchedule = years
    .map((y) => {
      const expiresOn = new Date(y + 10, 11, 31);
      const atMonth = Math.max(0, monthsBetween(now, expiresOn)); // 0 if already passed
      return {
        year: y,
        expiresOn, // Date
        atMonth, // months from now this slice “falls off”
        amount: perSlice, // equal allocation; purely illustrative
      };
    })
    // Keep only future (or present) drops so charts don’t go negative immediately
    .filter((d) => d.atMonth > 0)
    .sort((a, b) => a.atMonth - b.atMonth);

  return { months, expirations, dropSchedule };
}

// cheap deep clone for plain data (strings/numbers/arrays/objects)
const calcDisposable = (incomeData = {}, totalExpensesData = {}) => {
  const income = toNum(incomeData.totalIncome);
  const expenses = toNum(totalExpensesData.totalExpenses);

  let di = income - expenses;
  const negativeDisposableIncome = Math.min(di, 0);
  di = Math.max(0, di);

  return {
    disposableIncome: di,
    negativeDisposableIncome,
    // keeps old field name your UI might read
    monthlyCollectionAmount: di,
  };
};

const LIQUID_TYPES = new Set([
  "Savings Account",
  "Retirement Account",
  "Stocks and Bonds",
]);

function netAssetValue(a) {
  const val = toNum(a?.value);
  const liab = toNum(a?.assetLiab);
  return Math.max(0, val - liab);
}

function calculateOfferDetails({
  disposableIncome,
  assets = null,
  assetsDetails = null,
}) {
  const di = toNum(disposableIncome);
  const minApplicationOffer = 206; // app fee + $1, per your rule

  // Derive LIQUID assets (Savings, Retirement, Stocks/Bonds) -> 80%
  let liquidGross = 0;
  if (Array.isArray(assets)) {
    for (const a of assets) {
      if (LIQUID_TYPES.has(a?.assetType)) {
        liquidGross += netAssetValue(a);
      }
    }
  } else if (assetsDetails && typeof assetsDetails.netAssets !== "undefined") {
    // Fallback: if we only have netAssets, approximate liquid as all net assets
    liquidGross = toNum(assetsDetails.netAssets);
  }
  const liquidAssetValue = 0.8 * liquidGross;

  // Special case: no disposable income -> offer "value" is 206; no down payment
  if (di <= 0) {
    return {
      rcp: 0,
      liquidAssetValue,
      downPayment: 0,
      offerBasis: minApplicationOffer,
      minApplicationOffer,
      usedNoIncomeRule: true,
    };
  }

  // Regular case
  const rcp = 12 * di + liquidAssetValue;
  const downPayment = 0.2 * rcp;
  const offerBasis = Math.max(0.8 * rcp, minApplicationOffer); // keep your 0.8×RCP convention, floor at 206

  return {
    rcp,
    liquidAssetValue,
    downPayment,
    offerBasis,
    minApplicationOffer,
    usedNoIncomeRule: false,
  };
}

function calculateInstallmentDetails(
  disposableIncome,
  collectionWindowMonths,
  totalBalance
) {
  const di = Math.max(0, toNum(disposableIncome));
  const months = Math.max(1, toNum(collectionWindowMonths, 1));
  const balance = Math.max(0, toNum(totalBalance));

  // monthly proposal: 80% of disposable
  const paymentAmount = 0.8 * di;

  const installmentAgreementTotal = paymentAmount * months;
  const sixYearTotal = paymentAmount * 72;

  // band cutoffs
  const b20 = 0.2 * balance;
  const b80 = 0.8 * balance;
  const b120 = 1.2 * balance;

  let paymentPlanType = "";
  if (sixYearTotal >= balance) {
    paymentPlanType = "6-Year Payment";
  } else if (
    installmentAgreementTotal >= b80 &&
    installmentAgreementTotal <= b120
  ) {
    paymentPlanType = "DDIA";
  } else if (
    installmentAgreementTotal >= b20 &&
    installmentAgreementTotal < b80
  ) {
    paymentPlanType = "PPIA";
  } else {
    // <20% of balance covered in window ⇒ CNC
    paymentPlanType = "";
  }

  return {
    paymentAmount, // monthly
    installmentAgreementTotal, // window total
    sixYearTotal, // 72-month total
    paymentPlanType,
  };
}

/**
 * Determine the single best-fit settlement outcome from precomputed metrics.
 * Pass an object containing the properties listed below.
 */
function determineSettlement(m) {
  const reasons = [];
  const balance = Math.max(0, Number(m.totalBalance || 0));
  const months = Math.max(1, Number(m.months || 1));

  // Guard: if no balance, treat as Full Pay (nothing owed).
  if (balance === 0) {
    return {
      outcome: "Full Pay",
      monthlyPayment: 0,
      planMonths: 0,
      ratios: { windowCoverage: 1, sixYearCoverage: 1 },
      offer: {
        rcp: Number(m.rcp || 0),
        downPayment: Number(m.downPayment || 0),
        offerBasis: Number(m.offerBasis || m.minApplicationOffer || 0),
        liquidAssetValue: Number(m.liquidAssetValue || 0),
        usedNoIncomeRule: !!m.usedNoIncomeRule,
      },
      abatementEstimate: 0,
      reasons: ["No balance provided."],
    };
  }

  // Basics
  const paymentAmount = Math.max(0, Number(m.paymentAmount || 0));
  const installmentAgreementTotal = Math.max(
    0,
    Number(m.installmentAgreementTotal || 0)
  );
  const sixYearTotal = Math.max(0, Number(m.sixYearTotal || 0));

  // Ratios
  const windowCoverage = balance > 0 ? installmentAgreementTotal / balance : 0;
  const sixYearCoverage = balance > 0 ? sixYearTotal / balance : 0;

  // Approximate disposable from your convention: paymentAmount ≈ 0.8 * DI
  const diApprox = paymentAmount > 0 ? paymentAmount / 0.8 : 0; // conservative back-solve

  // OIC context
  const rcp = Math.max(0, Number(m.rcp || 0));
  const downPayment = Math.max(0, Number(m.downPayment || 0));
  const offerBasis = Math.max(
    0,
    Number(m.offerBasis || m.minApplicationOffer || 0)
  );
  const liquidAccounts = Math.max(0, Number(m.liquidAccounts || 0));

  // Very conservative OIC affordability: only liquid accounts (80%) plus
  // up to ~3 months of DI (you can drop +3*DI if you want even stricter).
  const downPaymentAffordable =
    0.8 * liquidAccounts + 3 * diApprox >= downPayment;

  const oicCandidate =
    rcp <= 1000 && windowCoverage < 0.2 && downPaymentAffordable;

  // Optional abatement “soft estimate” for IA types (do NOT promise)
  const abatementEstimate = 0.1 * Math.min(balance, installmentAgreementTotal);
  if (diApprox <= 0 || windowCoverage < 0.2) {
    if (diApprox <= 0) reasons.push("Disposable income is ≤ $0.");
    if (windowCoverage < 0.2)
      reasons.push("Coverage in the collection window is < 20%.");
    reasons.push("OIC down-payment screen not cleared.");
    return {
      outcome: "CNC",
      monthlyPayment: 0,
      planMonths: 0,
      ratios: { windowCoverage, sixYearCoverage },
      offer: {
        rcp,
        downPayment,
        offerBasis,
        liquidAssetValue: Number(m.liquidAssetValue || 0),
        usedNoIncomeRule: !!m.usedNoIncomeRule,
      },
      abatementEstimate: 0,
      reasons,
    };
  }
  // ── Selection order ─────────────────────────────────────────────
  // 1) OIC (only if extremely strong candidate)
  if (oicCandidate) {
    reasons.push(
      "Very low reasonable collection potential (≤ $1,000).",
      "Down payment appears affordable from liquid resources.",
      "Coverage over the collection window is < 20%."
    );
    return {
      outcome: "OIC",
      monthlyPayment: undefined,
      planMonths: undefined,
      ratios: { windowCoverage, sixYearCoverage },
      offer: {
        rcp,
        downPayment,
        offerBasis,
        liquidAssetValue: Number(m.liquidAssetValue || 0),
        usedNoIncomeRule: !!m.usedNoIncomeRule,
      },
      abatementEstimate: 0, // not applicable here
      reasons,
    };
  }

  // 2) CNC: little to no ability to pay (coverage <20% OR DI≤0),
  //    and OIC gate was not met.

  // 3) 6-Year Payment (72 months can fully repay)
  if (sixYearCoverage >= 1) {
    reasons.push("Six-year total covers or exceeds the full balance.");
    return {
      outcome: "6-Year Payment",
      monthlyPayment: paymentAmount,
      planMonths: 72,
      ratios: { windowCoverage, sixYearCoverage },
      offer: undefined,
      abatementEstimate,
      reasons,
    };
  }

  // 4) DDIA: window coverage ~ 80–120%
  if (windowCoverage >= 0.8 && windowCoverage <= 1.2) {
    reasons.push(
      "Payments over the collection window are roughly equal to the balance (80–120%)."
    );
    return {
      outcome: "DDIA",
      monthlyPayment: paymentAmount,
      planMonths: months,
      ratios: { windowCoverage, sixYearCoverage },
      offer: undefined,
      abatementEstimate,
      reasons,
    };
  }

  // 5) PPIA: window coverage 20–80%
  if (windowCoverage >= 0.2 && windowCoverage < 0.8) {
    reasons.push(
      "Payments over the collection window cover 20–80% of the balance."
    );
    if (Array.isArray(m.dropSchedule) && m.dropSchedule.length > 0) {
      reasons.push("Some debts may expire during the window (CSED effects).");
    }
    return {
      outcome: "PPIA",
      monthlyPayment: paymentAmount,
      planMonths: months,
      ratios: { windowCoverage, sixYearCoverage },
      offer: undefined,
      abatementEstimate,
      reasons,
    };
  }

  // 6) Fallback: Full Pay (rarely reached in practice with the above gates)
  reasons.push("Remaining rules imply the balance is fully payable.");
  return {
    outcome: "Full Pay",
    monthlyPayment: Math.max(paymentAmount, balance / 12), // ensure a coherent figure
    planMonths: 12,
    ratios: { windowCoverage, sixYearCoverage },
    offer: undefined,
    abatementEstimate: 0,
    reasons,
  };
}
export function computeSettlement(form) {
  // never hand out the original reference

  const allowances = determineAllowances(form.expenseData || {});
  const incomeData = calcIncome(form);
  const liabilitiesData = calcLiabilities(form);
  const assetsDetails = calcAssets(form);
  const collectionWindowDetails = calcCollectionWindow(form);
  const totalExpensesData = calcExpenses(allowances, liabilitiesData);
  const disposableIncome = calcDisposable(incomeData, totalExpensesData);
  const months = collectionWindowDetails?.months || 0;
  const installmentDetails = calculateInstallmentDetails(
    disposableIncome.monthlyCollectionAmount,
    months,
    form.totalBalance
  );
  const offerDetails = calculateOfferDetails(
    disposableIncome,
    form.assets,
    assetsDetails
  );

  const settlementCriteria = {
    totalBalance: form.totalBalance,
    months: collectionWindowDetails.months,
    paymentAmount: installmentDetails.paymentAmount,
    installmentAgreementTotal: installmentDetails.installmentAgreementTotal,
    sixYearTotal: installmentDetails.sixYearTotal,
    liquidAccounts: assetsDetails.liquidAccounts,
    rcp: offerDetails.rcp,
    downPayment: offerDetails.downPayment,
    offerBasis: offerDetails.offerBasis,
    minApplicationOffer: offerDetails.minApplicationOffer,
    usedNoIncomeRule: offerDetails.usedNoIncomeRule,
    dropSchedule: collectionWindowDetails.dropSchedule,
    expirations: collectionWindowDetails.expirations,
  };

  const settlementDetails = determineSettlement(settlementCriteria);
  return {
    ...form, // cloned, safe
    ...allowances, // copies are fine (spread or clone)
    ...incomeData,
    ...liabilitiesData,
    ...assetsDetails,
    ...collectionWindowDetails,
    ...totalExpensesData,
    ...disposableIncome,
    ...months,
    ...installmentDetails,
    ...offerDetails,
    ...settlementDetails,
  };
}
